import React, { useEffect, useState, useCallback, useRef } from "react";
import { Col, Row } from "reactstrap";
import ApiService from "../../services/apiService";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import { toast } from "react-toastify";
import { LucideRefreshCcw } from "lucide-react";

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY       = "#1e563d";
const PUMP_BLUE     = "#185FA5";
const PUMP_BLUE_BG  = "#E6F1FB";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SensorData {
  id:                   number;
  deviceId:             number;
  deviceName:           string;
  deviceType:           string;
  instrumentName:       string;
  instrumentNumber:     number | null;
  dateTime:             string | null;
  packetType:           string | null;
  classType:            string | null;
  firmwareVersion:      number | null;
  batteryVoltage:       number | null;
  temperature:          number | null;
  status:               number | null;
  highTemperatureAlarm: number | null;
  lowBatteryAlarm:      number | null;
  alarmBit:             number | null;
  moistureBit:          number | null;
  auto_Manual:          number | null;
  readSwitchHigh:       number | null;
  dry:                  number | null;
  wet:                  number | null;
  dry_SetPoint:         number | null;
  wet_SetPoint:         number | null;
  moisturePercentage:   number | null;
  mivc:                 number | null;
  mxvc:                 number | null;
  doorSignal:           number | null;
  waterMeterSignal:     number | null;
  s_ClassChange:        number | null;
  rssi:                 number | null;
  snr:                  number | null;
  spreadingFactor:      number | null;
}

export interface TimelineEvent {
  id:            number | string;
  instrumentId:  number | string;
  eventType:     string;
  operationType: string;
  dateTime:      string;
  alarmEvent:    number;
  description?:  string | null;
  userName?:     string | null;
  autoManual?: string | null;
  instrumentName?: string | null;
}

interface SensorDetailProps {
  id:            number | string;
  historyTable?: React.ReactNode;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function safeNum(v: number | null | undefined, s = ""): string {
  if (v == null || isNaN(v as number)) return "—";
  return `${v}${s}`;
}
function safeStr(v: string | null | undefined): string {
  return !v || v.trim() === "" ? "—" : v;
}
function fmt(dt: string | null | undefined, mode: "full" | "time" | "date" = "full"): string {
  if (!dt) return "—";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return "—";
  if (mode === "time") return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  if (mode === "date") return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}
function bitLabel(v: number | null | undefined, on: string, off: string): string {
  return v === 1 ? on : v === 0 ? off : "—";
}
function groupByDate(evs: TimelineEvent[]) {
  const m = new Map<string, TimelineEvent[]>();
  evs.forEach(ev => {
    const k = fmt(ev.dateTime, "date");
    if (!m.has(k)) m.set(k, []);
    m.get(k)!.push(ev);
  });
  return Array.from(m.entries()).map(([date, items]) => ({ date, items }));
}
function moistureColor(pct: number | null): string {
  if (pct === null) return "#94a3b8";
  if (pct >= 70)    return PUMP_BLUE;
  if (pct >= 40)    return PRIMARY;
  return "#854F0B";
}
function moistureLabel(pct: number | null, dry: number | null, wet: number | null): string {
  if (wet === 1)    return "Wet";
  if (dry === 1)    return "Dry";
  if (pct === null) return "—";
  if (pct >= 70)    return "Very Wet";
  if (pct >= 40)    return "Normal";
  return "Dry";
}

// ─── Timeline event config ────────────────────────────────────────────────────

function evCfg(operationType: string, alarmEvent: number): {
  dot: string; bg: string; border: string; category: string;
} {
  const op = operationType.toLowerCase().trim();

  if (alarmEvent === 1) {
    if (op.includes("temp"))    return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "alarm" };
    if (op.includes("battery")) return { dot: "#A32D2D", bg: "#fef2f2", border: "#fca5a5", category: "alarm" };
    if (op.includes("dry"))     return { dot: "#854F0B", bg: "#faeeda", border: "#fde68a", category: "alarm" };
    if (op.includes("wet"))     return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "alarm" };
    return                             { dot: "#A32D2D", bg: "#fef2f2", border: "#fca5a5", category: "alarm" };
  }

  if (op.includes("wet"))      return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "moisture" };
  if (op.includes("dry"))      return { dot: "#854F0B", bg: "#faeeda",    border: "#fde68a", category: "moisture" };
  if (op.includes("optimal"))  return { dot: PRIMARY,   bg: "#e8f2ed",    border: "#c6ddd3", category: "moisture" };
  if (op.includes("auto"))     return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "mode" };
  if (op.includes("manual"))   return { dot: "#854F0B", bg: "#faeeda",    border: "#fde68a", category: "mode" };
  if (op.includes("remote"))   return { dot: "#0F6E56", bg: "#E1F5EE",    border: "#9FE1CB", category: "mode" };
  if (op.includes("local"))    return { dot: "#64748b", bg: "#f1f5f9",    border: "#e2e8f0", category: "mode" };
  if (op.includes("command"))  return { dot: PUMP_BLUE, bg: PUMP_BLUE_BG, border: "#bfdbfe", category: "command" };
  return                              { dot: "#94a3b8", bg: "#f8fafc",    border: "#e2e8f0", category: "other" };
}

const TL_FILTERS = [
  { key: "all",      label: "All",      match: (_ev: TimelineEvent) => true },
  { key: "moisture", label: "Moisture", match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "moisture" },
  { key: "alarm",    label: "Alarms",   match: (ev: TimelineEvent) => ev.alarmEvent === 1 || evCfg(ev.operationType, ev.alarmEvent).category === "alarm" },
  { key: "mode",     label: "Mode",     match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "mode" },
  { key: "command",  label: "Commands", match: (ev: TimelineEvent) => evCfg(ev.operationType, ev.alarmEvent).category === "command" },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const DataRow: React.FC<{
  label:       string;
  value:       React.ReactNode;
  valueColor?: string;
  alarm?:      boolean;
  last?:       boolean;
}> = ({ label, value, valueColor, alarm, last }) => (
  <tr className="row-values">
    <td  className="td-label">{label}</td>
    <td  className="td-value">
      {alarm && (
        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#E24B4A", marginRight: 5, verticalAlign: "middle" }} />
      )}
      {value}
    </td>
  </tr>
);

const SectionHead: React.FC<{ title: string }> = ({ title }) => (
  <div className="section-head">{title}</div>
);

const Sig: React.FC<{ rssi: number | null | undefined }> = ({ rssi }) => {
  const v = rssi != null && !isNaN(rssi as number);
  const s = !v ? 0 : rssi! >= -70 ? 4 : rssi! >= -80 ? 3 : rssi! >= -90 ? 2 : 1;
  const c = s === 4 ? PRIMARY : s === 3 ? "#3B6D11" : s === 2 ? "#854F0B" : "#E24B4A";
  return (
    <span style={{ display: "inline-flex", alignItems: "flex-end", gap: 2 }}>
      {[4, 7, 10, 13].map((h, i) => (
        <span key={i} style={{ display: "inline-block", width: 3, height: h, borderRadius: 1, background: i < s ? c : "#e2e8f0" }} />
      ))}
      <span style={{ fontSize: 12, color: v ? c : "#94a3b8", marginLeft: 4 }}>{v ? `${rssi} dBm` : "—"}</span>
    </span>
  );
};

/** Moisture bar — inline for the data table */
const MoistureBar: React.FC<{
  pct:         number | null;
  dry:         number | null;
  wet:         number | null;
  drySetPoint: number | null;
  wetSetPoint: number | null;
}> = ({ pct, dry, wet, drySetPoint, wetSetPoint }) => {
  const fill   = pct !== null ? Math.max(0, Math.min(100, pct)) : 0;
  const mColor = moistureColor(pct);
  const mLabel = moistureLabel(pct, dry, wet);

  return (
    <div style={{ padding: "10px 0 6px" }}>
      {/* Percentage + label */}
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 32, fontWeight: 700, color: mColor, lineHeight: 1 }}>
          {pct !== null ? `${pct.toFixed(1)}%` : "—"}
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6,
          background: `${mColor}18`, color: mColor,
        }}>
          {mLabel}
        </span>
      </div>

      {/* Track */}
      <div style={{ position: "relative", marginBottom: 25 }}>
        <div style={{ height: 9, background: "#e2e8f0", borderRadius: 5, overflow: "hidden" }}>
          <div style={{ width: `${fill}%`, height: "100%", background: mColor, borderRadius: 5, transition: "width 0.4s" }} />
        </div>
        {drySetPoint !== null && (
          <div style={{ position: "absolute", top: -3, left: `${drySetPoint}%`, transform: "translateX(-50%)" }}>
            <div style={{ width: 2, height: 15, background: "#854F0B" }} />
            <div style={{ fontSize: 14, color: "#854F0B", whiteSpace: "nowrap", marginTop: 1, transform: "translateX(-20%)" }}>
              Dry {drySetPoint}%
            </div>
          </div>
        )}
        {wetSetPoint !== null && (
          <div style={{ position: "absolute", top: -3, left: `${wetSetPoint}%`, transform: "translateX(-50%)" }}>
            <div style={{ width: 2, height: 15, background: PUMP_BLUE }} />
            <div style={{ fontSize: 14, color: PUMP_BLUE, whiteSpace: "nowrap", marginTop: 1, transform: "translateX(-20%)" }}>
              Wet {wetSetPoint}%
            </div>
          </div>
        )}
      </div>

      {/* Dry / Wet chips */}
      {/* <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <div style={{
          flex: 1, padding: "6px 8px", borderRadius: 7, textAlign: "center",
          background: dry === 1 ? "#faeeda" : "#f8fafc",
          border:     `1px solid ${dry === 1 ? "#fde68a" : "#e2e8f0"}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: dry === 1 ? "#854F0B" : "#94a3b8" }}>Dry</div>
          <div style={{ fontSize: 10, color: dry === 1 ? "#854F0B" : "#94a3b8", marginTop: 1 }}>
            {dry === 1 ? "Active" : "—"}
          </div>
        </div>
        <div style={{
          flex: 1, padding: "6px 8px", borderRadius: 7, textAlign: "center",
          background: wet === 1 ? PUMP_BLUE_BG : "#f8fafc",
          border:     `1px solid ${wet === 1 ? "#bfdbfe" : "#e2e8f0"}`,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: wet === 1 ? PUMP_BLUE : "#94a3b8" }}>Wet</div>
          <div style={{ fontSize: 10, color: wet === 1 ? PUMP_BLUE : "#94a3b8", marginTop: 1 }}>
            {wet === 1 ? "Active" : "—"}
          </div>
        </div>
      </div> */}
    </div>
  );
};

/** Timeline date divider */
const DateDiv: React.FC<{ date: string }> = ({ date }) => {
  const today = fmt(new Date().toISOString(), "date");
  const yest  = fmt(new Date(Date.now() - 86_400_000).toISOString(), "date");
  const label = date === today ? "Today" : date === yest ? "Yesterday" : date;
  return (
    <div style={styles.dateDivider}>
      <div style={styles.dateDivLine} />
      <span style={styles.dateDivLabel}>{label}</span>
      <div style={styles.dateDivLine} />
    </div>
  );
};

/** Timeline event card — A / E dot */
const TLCard: React.FC<{ ev: TimelineEvent; isLast: boolean }> = ({ ev, isLast }) => {
  const cfg     = evCfg(ev.operationType, ev.alarmEvent);
  const isAlarm = ev.alarmEvent === 1;
  const letter  = isAlarm ? "A" : "E";

  return (
    <div  className="tlRow">
      <div className="tlSpine" >
        <div className="tlDot" style={{  background: cfg.bg, border: `1.5px solid ${cfg.dot}` }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: cfg.dot, lineHeight: 1, fontFamily: "monospace" }}>
            {letter}
          </span>
        </div>
        {!isLast && <div  className="tlLine" />}
      </div>

      <div className="tlCard" style={{  marginBottom: isLast ? 0 : 6 }}>
        <div className="tlCardHead" >
          <span className="tlTag" style={{ background: cfg.bg, color: cfg.dot, border: `1px solid ${cfg.border}` }}>
            {ev.operationType}
          </span>
          <span className="tlTime" >{fmt(ev.dateTime, "time")}</span>
        </div>

        {(ev.userName || ev.autoManual || ev.instrumentName) && (
          <div  className="tlChips">
            {ev.userName && (
              <span className="tlChip" style={{background: "#f0f4ff", color: "#3b5bdb", border: "1px solid #c5d0e6" }}>
                {ev.userName}
              </span>
            )}
            {ev.autoManual && (
              <span className="tlChip" style={{

                background: ev.autoManual === "Auto Mode" ? PUMP_BLUE_BG : "#faeeda",
                color:      ev.autoManual === "Auto Mode" ? PUMP_BLUE    : "#854F0B",
                border:     `1px solid ${ev.autoManual === "Auto Mode" ? "#bfdbfe" : "#fde68a"}`,
              }}>
                {ev.autoManual}
              </span>
            )}

            {ev.instrumentName && (   // ✅ add this chip
                  <span className="tlChip" style={{ background: "#e8f2ed", color: PRIMARY, border: "1px solid #c6ddd3" }}>
                    {ev.instrumentName}
                  </span>
                )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── SensorDetail ─────────────────────────────────────────────────────────────

const SensorDetail: React.FC<SensorDetailProps> = ({ id, historyTable }) => {
  const [data,         setData]         = useState<SensorData | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [modeBusy,     setModeBusy]     = useState(false);

  // ── Timeline state ────────────────────────────────────────────────────────
  const [tlEvs,         setTlEvs]         = useState<TimelineEvent[]>([]);
  const [tlLoading,     setTlLoading]     = useState(true);
  const [tlLoadingMore, setTlLoadingMore] = useState(false);
  const [tlError,       setTlError]       = useState<string | null>(null);
  const [tlFilter,      setTlFilter]      = useState("all");
  const [tlRef,         setTlRef]         = useState(false);
  const [tlHasMore,     setTlHasMore]     = useState(true);
  const tlPageRef    = useRef(1);
  const tlScrollRef  = useRef<HTMLDivElement>(null);
  const tlSentinel   = useRef<HTMLDivElement>(null);
  const tlFetching   = useRef(false);
  const tlHasMoreRef = useRef(true);
  const PAGE_SIZE    = 10;

  // ── Helpers ───────────────────────────────────────────────────────────────

  const setHasMore = (v: boolean) => {
    tlHasMoreRef.current = v;
    setTlHasMore(v);
  };

  // ── Timeline loader ───────────────────────────────────────────────────────

  const loadTL = useCallback(async (page: number, append: boolean) => {
    if (!data?.id) return;
    if (tlFetching.current) return;
    tlFetching.current = true;

    if (page === 1) {
      setTlLoading(true);
      setTlError(null);
      setTlEvs([]);
    } else {
      setTlLoadingMore(true);
    }

    const now = new Date();
    const ist = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const isoIST =
      `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, "0")}-${String(ist.getDate()).padStart(2, "0")}T` +
      `${String(ist.getHours()).padStart(2, "0")}:${String(ist.getMinutes()).padStart(2, "0")}:${String(ist.getSeconds()).padStart(2, "0")}`;

    try {
      const res = await ApiService.axiosApi(`/timelinemaster/get-timeline`, {
        method: "GET",
        params: {
          instrumentId: data.id,
          dateTime:     isoIST,
          pageNumber:   page,
          pageSize:     PAGE_SIZE,
        },
      });

      if (res.success) {
        const raw: any[] = Array.isArray(res.data.data) ? res.data.data : [];
        const mapped: TimelineEvent[] = raw.map((item: any) => ({
          id:            item.id,
          instrumentId:  item.instrumentId ?? "",
          dateTime:      item.dateTime,
          alarmEvent:    item.alarm_Event ?? 0,
          operationType: item.operationType ?? "Unknown",
          eventType:     item.operationType?.toLowerCase().replace(/\s+/g, "_") ?? "unknown",
          userName:      item.userName    ?? null,
          autoManual:    item.auto_Manual ?? null,
          description: item.remark ?? null,
          instrumentName: item.instrumentName ?? null,   // ✅ add this

        }));

        setHasMore(raw.length === PAGE_SIZE);
        tlPageRef.current = page;
        setTlEvs(prev => append ? [...prev, ...mapped] : mapped);
      } else {
        setTlError("Failed to load timeline.");
      }
    } catch {
      setTlError("Network error.");
    } finally {
      setTlLoading(false);
      setTlLoadingMore(false);
      setTlRef(false);
      tlFetching.current = false;
    }
  }, [data?.id]);

  const refreshTL = useCallback(async () => {
    setTlRef(true);
    tlPageRef.current = 1;
    setHasMore(true);
    await loadTL(1, false);
  }, [loadTL]);

  // ── Data loader ───────────────────────────────────────────────────────────

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await ApiService.axiosApi(`/instrumentmaster/${id}`, { method: "GET" });
      if (res.success) setData(res.data.data ?? res.data);
      else setError("Failed to load sensor data.");
    } catch { setError("Network error."); }
    finally { setLoading(false); setRefreshing(false); }
  }, [id]);

  useEffect(() => { if (id) loadData(); }, [id, loadData]);

  useEffect(() => {
    if (data?.id) {
      tlPageRef.current = 1;
      setHasMore(true);
      loadTL(1, false);
    }
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Infinite scroll ───────────────────────────────────────────────────────

  useEffect(() => {
    const root   = tlScrollRef.current;
    const target = tlSentinel.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && tlHasMoreRef.current && !tlFetching.current) {
          loadTL(tlPageRef.current + 1, true);
        }
      },
      { root, threshold: 0, rootMargin: "300px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [tlEvs.length]);

  // ── INTG refresh ─────────────────────────────────────────────────────────

  const handleIntg = async () => {
    if (!data) return;
    setRefreshLoading(true);
    setModeBusy(true);
    try {
      const res = await ApiService.axiosApi("/valvecommand/intg", {
        method: "POST",
        data:   { instrumentId: data.id },
      });
      if (res.success) {
        toast.success("Refreshed");
        await loadData(true);
      } else {
        toast.error(res.message ?? "Refresh failed! Please try again later.");
      }
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong");
    } finally {
      setModeBusy(false);
      setRefreshLoading(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────

  if (loading) return (
    <div style={styles.stateBox}>
      <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 20, color: PRIMARY, animation: "vdspin .9s linear infinite" }} />
      <span style={styles.stateText}>Loading sensor data…</span>
      <style>{`@keyframes vdspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error || !data) return (
    <div style={{ ...styles.stateBox, border: "1px solid #fca5a5" }}>
      <i className="ti ti-alert-circle" aria-hidden="true" style={{ fontSize: 20, color: "#A32D2D" }} />
      <span style={{ ...styles.stateText, color: "#A32D2D" }}>{error ?? "No data found."}</span>
      <button onClick={() => loadData()} style={styles.retryBtn}>Retry</button>
    </div>
  );

  // ── Derived state ─────────────────────────────────────────────────────────

  const highTemp  = data.highTemperatureAlarm === 1;
  const lowBatt   = data.lowBatteryAlarm      === 1;
  const hasAlarm  = data.alarmBit             === 1;
  const anyAlarm  = hasAlarm || highTemp || lowBatt;
  const mColor    = moistureColor(data.moisturePercentage);

  const tlFiltered = tlEvs.filter(ev => TL_FILTERS.find(f => f.key === tlFilter)!.match(ev));
  const tlGrouped  = groupByDate(tlFiltered);

  return (
    <>
      <Breadcrumbs mainTitle="Sensor Detail" parent="User Lookup" />
      <style>{`
        @keyframes vdspin   { to { transform: rotate(360deg); } }
        @keyframes vdpulse  { 0%,100% { opacity:1 } 50% { opacity:.3 } }
        @keyframes spin     { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>

      <div style={styles.page}>

        {/* ════ 50/50 split ════ */}
        <div style={styles.splitRow}>

          {/* ──────────── LEFT: detail ──────────── */}
          <div className="left-panel">

            {/* Header */}
            <div className="detail-header">
              <div style={{
                ...styles.sensorIconBox,
                background: data.moistureBit === 1 ? PUMP_BLUE_BG : "#e8f2ed",
                border:     `1px solid ${data.moistureBit === 1 ? "#bfdbfe" : "#c6ddd3"}`,
              }}>
                <i
                  className="ti ti-radar"
                  aria-hidden="true"
                  style={{ fontSize: 22, color: data.moistureBit === 1 ? PUMP_BLUE : PRIMARY }}
                />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="instrument-name">{safeStr(data.instrumentName)}</div>
                <div className="instrument-meta">
                  {safeStr(data.deviceName)} · {fmt(data.dateTime)}



                </div>
              </div>

              {anyAlarm && (
                <div className="alarm-badge">
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#E24B4A", animation: "vdpulse 1.4s infinite" }} />
                  {hasAlarm && "Alarm "}
                  {highTemp && "High Temp "}
                  {lowBatt  && "Low Batt"}
                </div>
              )}

              <button className="iconBtn" onClick={handleIntg} disabled={refreshLoading}>
                <LucideRefreshCcw
                  size={14}
                  style={{ animation: refreshLoading ? "spin 1s linear infinite" : "none" }}
                />
              </button>
            </div>

            {/* Moisture bar strip — sits just below the header */}
            <div style={styles.moistureStrip}>
              <MoistureBar
                pct={data.moisturePercentage}
                dry={data.dry}
                wet={data.wet}
                drySetPoint={data.dry_SetPoint}
                wetSetPoint={data.wet_SetPoint}
              />
            </div>

            {/* Data grid */}
            <div className="data-block">
              <Row style={{ margin: 0 }}>

                {/* Column A */}
                <Col className="col-one" style={{ padding: "0 6px 0 10px", marginRight:'5px' }}>

                  <SectionHead title="Live readings" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Moisture %"    value={data.moisturePercentage !== null ? `${data.moisturePercentage}%` : "—"} valueColor={mColor} />
                      <DataRow label="Temperature"   value={safeNum(data.temperature, " °C")}    alarm={highTemp} />
                      <DataRow label="Battery"       value={safeNum(data.batteryVoltage, " V")}  alarm={lowBatt} />
                      <DataRow label="Moisture bit"  value={bitLabel(data.moistureBit, "Active", "Inactive")}  valueColor={data.moistureBit === 1 ? PUMP_BLUE : undefined} />
                      <DataRow label="Read switch"   value={bitLabel(data.readSwitchHigh, "High", "Low")} />
                      <DataRow label="Door signal"   value={bitLabel(data.doorSignal, "Active", "Inactive")} />
                      <DataRow label="Water meter"   value={bitLabel(data.waterMeterSignal, "Active", "Inactive")} />
                      <DataRow label="Class change"  value={bitLabel(data.s_ClassChange, "Yes", "No")} last />
                    </tbody>
                  </table>

                  <SectionHead title="Alarms" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="General alarm"    value={hasAlarm ? "Active" : "Clear"} alarm={hasAlarm} />
                      <DataRow label="High temperature" value={highTemp  ? "Active" : "Clear"} alarm={highTemp} />
                      <DataRow label="Low battery"      value={lowBatt   ? "Active" : "Clear"} alarm={lowBatt}  last />
                    </tbody>
                  </table>

                </Col>

                {/* Column B */}
                <Col className="col-one" style={{ padding: "0 6px 0 10px", marginRight:'5px' }}>

                  <SectionHead title="Signal" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="RSSI"             value={<Sig rssi={data.rssi} />} />
                      <DataRow label="SNR"              value={safeNum(data.snr, " dB")} />
                      <DataRow label="Spreading factor" value={safeNum(data.spreadingFactor)} last />
                    </tbody>
                  </table>

                  <SectionHead title="Set points" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Dry set point" value={data.dry_SetPoint !== null ? `${data.dry_SetPoint}%` : "—"} valueColor="#854F0B" />
                      <DataRow label="Wet set point" value={data.wet_SetPoint !== null ? `${data.wet_SetPoint}%` : "—"} valueColor={PUMP_BLUE} last />
                    </tbody>
                  </table>

                  <SectionHead title="Calibration" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Min voltage (MIVC)" value={safeNum(data.mivc)} />
                      <DataRow label="Max voltage (MXVC)" value={safeNum(data.mxvc)} last />
                    </tbody>
                  </table>

                  <SectionHead title="Device config" />
                  <table className="data-table">
                    <tbody>
                      <DataRow label="Packet type" value={safeStr(data.packetType)} />
                      <DataRow label="Class type"  value={safeStr(data.classType)} />
                      <DataRow label="Firmware"    value={safeNum(data.firmwareVersion)} last />
                    </tbody>
                  </table>

                </Col>
              </Row>
            </div>
          </div>

          {/* ──────────── RIGHT: timeline ──────────── */}
          <div className="rightPanel" >

            <div  className = "tlHeader">
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <i className="ti ti-clock-hour-3" aria-hidden="true" style={{ fontSize: 13, color: "#64748b" }} />
                <span  className="panelSectionTitle">Timeline</span>
              </div>
              <button onClick={refreshTL} className="iconBtn"  >
                <LucideRefreshCcw
                  size={14}
                  style={{ animation: tlRef ? "spin 1s linear infinite" : "none" }}
                />
              </button>
            </div>

            <div  className="tlFilters">
              {TL_FILTERS.map(f => {
                const cnt    = f.key === "all" ? tlEvs.length : tlEvs.filter(ev => f.match(ev)).length;
                const active = tlFilter === f.key;
                return (
                  <button
                    key={f.key}
                    onClick={() => {
                      setTlFilter(f.key);
                      tlPageRef.current = 1;
                      setTlHasMore(true);
                      loadTL(1, false);
                    }}
                    className = {`tlFilterBtn ${active ? 'tlFilterBtnActive' :'' }`}

                  >
                    {f.label}
                    <span className="tlFilterCount" style={{  background: active ? PRIMARY : "#e2e8f0", color: active ? "#fff" : "#64748b" }}>
                      {cnt}
                    </span>
                  </button>
                );
              })}
            </div>

            <div ref={tlScrollRef}  className="tlBody">
              {tlLoading ? (
                <div  className="tlState">
                  <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 16, color: PRIMARY, animation: "vdspin .9s linear infinite" }} />
                  <span style={{ fontSize: 12, color: "#64748b" }}>Loading…</span>
                </div>
              ) : tlError ? (
                <div  className="tlState">
                  <i className="ti ti-alert-circle" aria-hidden="true" style={{ fontSize: 14, color: "#A32D2D" }} />
                  <span style={{ fontSize: 12, color: "#A32D2D" }}>{tlError}</span>
                  <button onClick={() => loadTL(1, false)}  className="retryBtn">Retry</button>
                </div>
              ) : tlFiltered.length === 0 ? (
                <div className="tlState" style={{ flexDirection: "column" }}>
                  <i className="ti ti-clock-hour-3" aria-hidden="true" style={{ fontSize: 24, color: "#e2e8f0" }} />
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>No events</span>
                </div>
              ) : (
                <>
                  {tlGrouped.map(({ date, items }) => (
                    <div key={date}>
                      <DateDiv date={date} />
                      {items.map((ev, idx) => (
                        <TLCard key={ev.id} ev={ev} isLast={idx === items.length - 1} />
                      ))}
                    </div>
                  ))}

                  {tlHasMore && (
                    <div ref={tlSentinel} style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {tlLoadingMore && (
                        <i className="ti ti-loader-2" aria-hidden="true" style={{ fontSize: 14, color: "#94a3b8", animation: "vdspin .9s linear infinite" }} />
                      )}
                    </div>
                  )}
                  {!tlHasMore && tlEvs.length > 0 && (
                    <div style={{ textAlign: "center", padding: "10px 0 2px", fontSize: 10, color: "#cbd5e1" }}>
                      — All {tlEvs.length} events loaded —
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>

        {/* ════ History table — full width ════ */}
        {historyTable && (
          <div className="historyPanel">
            <div className ='historyHeader'>
              <span className='historySectionTitle'>History</span>
            </div>
            <div style={styles.historyBody}>{historyTable}</div>
          </div>
        )}

      </div>
    </>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {

  page: {
    display:       "flex",
    flexDirection: "column",
    gap:           10,
    paddingBottom: 24,
  },

  splitRow: {
    display:    "flex",
    gap:        10,
    alignItems: "stretch",
    height:     "750px",
  },

  // ── Left ──
  leftPanel: {
    flex:          "0 0 50%",
    width:         "50%",
    background:    "#fff",
    border:        "1px solid #e8edf2",
    borderRadius:  10,
    overflow:      "hidden",
    display:       "flex",
    flexDirection: "column",
    height:        "100%",
  },

  detailHeader: {
    display:         "flex",
    alignItems:      "center",
    gap:             12,
    padding:         "14px 16px",
    borderBottom:    "1px solid #f1f5f9",
    backgroundColor: PRIMARY,
  },

  sensorIconBox: {
    width:          48,
    height:         48,
    borderRadius:   10,
    flexShrink:     0,
    display:        "flex",
    alignItems:     "center",
    justifyContent: "center",
  },

  instrumentName: {
    fontSize:     16,
    fontWeight:   600,
    color:        "#fff",
    whiteSpace:   "nowrap" as const,
    overflow:     "hidden",
    textOverflow: "ellipsis",
  },
  instrumentMeta: {
    fontSize:  13,
    color:     "#fff",
    marginTop: 2,
  },

  alarmBadge: {
    display:      "flex",
    alignItems:   "center",
    gap:          5,
    padding:      "4px 10px",
    borderRadius: 8,
    fontSize:     11,
    fontWeight:   500,
    background:   "#fef2f2",
    color:        "#A32D2D",
    border:       "1px solid #fca5a5",
    flexShrink:   0,
  },

  iconBtn: {
    display:         "inline-flex",
    alignItems:      "center",
    justifyContent:  "center",
    width:           30,
    height:          30,
    borderRadius:    7,
    backgroundColor: "#e8f2ed",
    border:          "1px solid #e2e8f0",
    cursor:          "pointer",
  },

  // Moisture strip — between header and data block
  moistureStrip: {
    padding:      "8px 16px 4px",
    borderBottom: "1px solid #f1f5f9",
  },

  dataBlock: {
    flex:      1,
    overflowY: "auto",
    minHeight: 0,
  },

  sectionHead: {
    fontSize:      13,
    fontWeight:    700,
    color:         "#334155",
    textTransform: "uppercase" as const,
    letterSpacing: "0.07em",
    padding:       "10px 0 5px",
    borderBottom:  "1px solid #e8edf2",
    marginBottom:  0,
  },
  dataTable: {
    width:          "100%",
    borderCollapse: "collapse" as const,
    marginBottom:   8,
  },
  tdLabel: {
    fontSize: 13,
    color:    "#64748b",
    padding:  "6px 0",
    width:    "55%",
  },
  tdValue: {
    fontSize:   12,
    fontWeight: 700,
    color:      "#1e293b",
    textAlign:  "right" as const,
    padding:    "6px 0",
  },



  dateDivider:  { display: "flex", alignItems: "center", gap: 8, margin: "8px 0 6px" },
  dateDivLine:  { flex: 1, height: 1, background: "#f1f5f9" },
  dateDivLabel: { fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase" as const, letterSpacing: "0.08em", whiteSpace: "nowrap" as const },

  // ── History ──
  historyPanel:  { background: "#fff", border: "1px solid #e8edf2", borderRadius: 10, overflow: "hidden" },
  historyHeader: { display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" },
  historyTitle:  { fontSize: 13, fontWeight: 600, color: "#334155", textTransform: "uppercase" as const, letterSpacing: "0.07em" },
  historyBody:   { padding: "12px 14px" },

  // ── Generic states ──
  stateBox:  { display: "flex", alignItems: "center", gap: 10, padding: 28, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10 },
  stateText: { fontSize: 13, color: "#64748b" },
  retryBtn:  { marginLeft: 8, padding: "4px 12px", borderRadius: 7, fontSize: 11, background: "#fef2f2", color: "#A32D2D", border: "1px solid #fca5a5", cursor: "pointer" },
};

export default SensorDetail;
